<template>
  <g class="AreaChart" :transform="`translate(${left}, ${top})`">
    <g
      class="AreaChart-content"
      :transform="`translate(${paddingX}, ${paddingY})`"
    >
      <template v-if="showArea">
        <datalas-area-path
          v-for="(path, i) in paths"
          :key="'area' + i"
          v-bind="path"
        />
      </template>

      <template v-if="showLine">
        <datalas-line-path
          v-for="(path, i) in paths"
          :key="'line' + i"
          v-bind="path"
        />
      </template>

      <template v-if="showDots">
        <datalas-dot-path
          v-for="(path, i) in paths"
          :key="'dot' + i"
          v-bind="path"
          @datum:mouseover="$emit('datum:mouseover', $event)"
          @datum:mouseout="$emit('datum:mouseout', $event)"
          @datum:click="$emit('datum:click', $event)"
        />
      </template>

      <datalas-labels
        v-if="showLabels"
        :labels="labels"
        :prefix="labelPrefix"
        :suffix="labelSuffix"
        :format="labelFormat"
        :font-size="labelFontSize"
        :detect-collisions="noLabelCollisions"
        :filters="labelFilters"
        :offset-x="labelOffsetX"
        :offset-y="labelOffsetY"
      />
    </g>

    <teleport v-if="gridMounted" :to="`#${gridKey}`">
      <datalas-grid v-bind="gridLayout" />
    </teleport>
  </g>
</template>

<script>
import Layer from './layer.vue';
import DatalasAreaPath from '../datum/area-path.vue';
import DatalasLinePath from '../datum/line-path.vue';
import DatalasDotPath from '../datum/dot-path.vue';

export default {
  name: 'DatalasArea',

  extends: Layer,

  components: {
    DatalasAreaPath,
    DatalasLinePath,
    DatalasDotPath,
  },
  emits: ['datum:mouseover', 'datum:mouseout', 'datum:click'],
  props: {
    align: {
      type: String,
    },
    showLine: {
      type: Boolean,
    },
    showArea: {
      type: Boolean,
    },
    showDots: {
      type: Boolean,
    },
    showBands: {
      type: Boolean,
    },
    isStacked: {
      type: Boolean,
    },
    dotSize: {
      type: Number,
    },
    lineSize: {
      type: Number,
    },
    messageField: {
      type: String,
    },
    dimensions: {
      type: Array,
      required: true,
      // TODO: validate
    },
  },

  computed: {
    xTransform() {
      return this.getDimensionTransform('x');
    },
    yTransform() {
      return this.getDimensionTransform('y');
    },
    fillTransform() {
      return this.getDimensionTransform('fill');
    },
    y0() {
      return {
        top: 0,
        right: this.width,
        bottom: this.height,
        left: 0,
      }[this.alignValue];
    },
    xDomain() {
      return this.getDomain(this.xAccessor, this.xTransform.options);
    },
    yDomain() {
      return this.getDomain(this.yAccessor, this.yTransform.options);
    },
    fillValues() {
      const { field } = this.fillTransform;
      return Object.keys(
        this.data.reduce((acc, d) => ({ ...acc, [d[field]]: null }), {}),
      );
    },
    fillDomain() {
      return this.fillTransform.options.type === 'scheme'
        ? this.fillValues
        : undefined;
    },
    isSideways() {
      return this.alignValue === 'left' || this.alignValue === 'right';
    },
    xScale() {
      return this.xTransform.transformer(this.xDomain, [
        0,
        this.isSideways ? this.height : this.width,
      ]);
    },
    yScale() {
      return this.yTransform.transformer(this.yDomain, [
        this.y0,
        (this.isSideways ? this.width : this.height) - this.y0,
      ]);
    },
    fillScale() {
      return this.fillTransform.transformer(this.fillDomain);
    },
    bandwidth() {
      return this.showBands && this.xScale.bandwidth
        ? this.xScale.bandwidth()
        : 0;
    },
    stack() {
      return this.$d3.stack().keys(this.fillValues);
    },
    nested() {
      return this.$d3
        .nest()
        .key((d) => d[this.fillTransform.field])
        .entries(this.data);
    },
    munged() {
      // Prepare data for d3.stack.
      return Object.values(
        this.data.reduce((acc, d) => {
          const x = this.xAccessor(d);
          const y = this.yAccessor(d);
          const fill = this.fillAccessor(d);

          acc[x] = acc[x] || {};
          acc[x][this.xTransform.field] = x;
          acc[x][fill] = y;

          return acc;
        }, {}),
      );
    },
    stacked() {
      return this.stack(this.munged);
    },
    paths() {
      return this.nested.map((group, i) => {
        const fill = this.fillScale(group.key);
        const stack = this.stacked[i];

        return {
          fill,
          stroke: fill,
          data: group.values.map((d, j) => ({
            ...d, // TODO: don't mix data with presentation
            y0: stack[j] ? stack[j][0] : 0,
            y1: stack[j] ? stack[j][1] : 0,
            datum: d,
          })),
          xAccessor: this.xAccessor,
          y0Accessor: (d) =>
            this.isStacked ? d.y0 : this.yTransform.options.min,
          yAccessor: (d) => (this.isStacked ? d.y1 : this.yAccessor(d)),
          xScale: this.xScale,
          yScale: this.yScale,
          bandwidth: this.bandwidth,
          isSideways: this.isSideways,
          strokeWidth: this.dotSize / 7.0,
          lineSize: this.lineSize / 7.0,
        };
      });
    },
    labels() {
      return this.paths.reduce(
        (acc, path) => [
          ...acc,
          ...path.data.map((datum) => ({
            x: path.xScale(path.xAccessor(datum)),
            y: path.yScale(path.yAccessor(datum)),
            value: path.yAccessor(datum),
            textAnchor: this.labelAnchorVal,
            dominantBaseline: this.labelBaselineVal,
            datum,
          })),
        ],
        [],
      );
    },

    gridLayout() {
      const grid = { ...this.grid };
      grid.axes = grid.axes.map((axis) => {
        const props = { ...axis.props };
        const x = this.dimensions.find((d) => d.dimension === 'x').transform;
        props.scale = props.scale === x ? this.xScale : this.yScale;
        props.height = this.height;
        props.width = this.width;
        return { ...axis, props };
      });
      return grid;
    },
  },

  methods: {
    xAccessor(item) {
      return item[this.xTransform.field];
    },
    yAccessor(item) {
      return item[this.yTransform.field];
    },
    fillAccessor(item) {
      return item[this.fillTransform.field];
    },

    getDomain(accessor, options) {
      if (options.type === 'continuous') {
        const { min, max } = options;
        return [
          min === undefined || min === null
            ? this.$d3.min(this.data, accessor)
            : min,
          max === undefined || max === null
            ? this.$d3.max(this.data, accessor)
            : max,
        ];
      }

      // Assume categorical scale
      return Object.keys(
        this.data
          .map(accessor)
          .reduce((acc, x) => Object.assign(acc, { [x]: true }), {}),
      );
    },
  },
};
</script>
